import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Autocomplete,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Avatar,
  CircularProgress,
  Radio,
} from '@mui/material';
import { 
  Search as SearchIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Visibility,
  Close,
  Person,
  CurrencyRupee as CurrencyRupeeIcon,
  CalendarToday,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  FilterAlt,
  DateRange,
  Today,
  CalendarMonth,
  FileDownload,
} from '@mui/icons-material';
import { getBookingCommission } from '../../services/reports';
import { getAssociates } from '../../services/associate';
import { getBookings } from '../../services/project';
import ExportButton from '../../components/ExportButton/ExportButton';
import RefreshButton from '../../components/RefreshButton/RefreshButton';
import FormatBadge from '../../components/FormatBadge/FormatBadge';
import { showSnackbar } from '../../components/Snackbar';
import { exportAsPDF, exportAsCSV, exportAsExcel } from '../../utils/importExport';
import AlertModal from '../../components/AlertModal';
import MoreFilters from '../../components/MoreFilters/MoreFilters';


const BookingCommission = () => {
  const history = useHistory();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    status: '',
    associate: '',
    searchTerm: '',
    date: '',
  });
  const [selectedAssociate, setSelectedAssociate] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);

  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedCommission, setSelectedCommission] = useState(null);

  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);
  const [sortBy, setSortBy] = useState('');
  const [tempSortBy, setTempSortBy] = useState('');
  const [tempFromDate, setTempFromDate] = useState('');
  const [tempToDate, setTempToDate] = useState('');
  const [tempDate, setTempDate] = useState('');
  const [tempSelectedBooking, setTempSelectedBooking] = useState(null);
  const [tempSelectedCustomer, setTempSelectedCustomer] = useState(null);

  // Export Dialog States
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportType, setExportType] = useState('');
  const [exportDateMode, setExportDateMode] = useState('current');
  const [exportFromDate, setExportFromDate] = useState('');
  const [exportToDate, setExportToDate] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const [alertModal, setAlertModal] = useState({ open: false, title: 'Alert', message: '' });
  const closeAlert = () => setAlertModal({ open: false, title: 'Alert', message: '' });

  // API data states
  const [commissionData, setCommissionData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // Autocomplete states
  const [associateOptions, setAssociateOptions] = useState([]);
  const [associateSearchInput, setAssociateSearchInput] = useState('');
  const [loadingAssociates, setLoadingAssociates] = useState(false);
  const associateFetchingRef = useRef(false);
  const associateInitialLoadRef = useRef(false);
  const [associatePage, setAssociatePage] = useState(1);
  const [associateHasMore, setAssociateHasMore] = useState(true);
  const associateSearchTermRef = useRef('');
  
  const [bookingOptions, setBookingOptions] = useState([]);
  const [bookingSearchInput, setBookingSearchInput] = useState('');
  const [loadingBookings, setLoadingBookings] = useState(false);
  const bookingFetchingRef = useRef(false);
  const bookingInitialLoadRef = useRef(false);
  const [bookingPage, setBookingPage] = useState(1);
  const [bookingHasMore, setBookingHasMore] = useState(true);
  const bookingSearchTermRef = useRef('');
  
  // Customer dropdown states
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerSearchInput, setCustomerSearchInput] = useState('');
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const customerFetchingRef = useRef(false);
  const customerInitialLoadRef = useRef(false);
  const [customerPage, setCustomerPage] = useState(1);
  const [customerHasMore, setCustomerHasMore] = useState(true);
  const customerSearchTermRef = useRef('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  useEffect(() => {
    if (moreFiltersOpen) {
      setTempSortBy(sortBy);
      setTempFromDate(filters.fromDate || '');
      setTempToDate(filters.toDate || '');
      setTempDate(filters.date || '');
      setTempSelectedBooking(selectedBooking);
      setTempSelectedCustomer(selectedCustomer);
    }
  }, [moreFiltersOpen, sortBy, filters, selectedBooking, selectedCustomer]);


  // Map API response to component data structure
  const mapCommissionData = (item, index, currentPage, currentRowsPerPage) => {
    const plotAmount = item.plotAmount || 0;
    
    return {
      sno: currentPage * currentRowsPerPage + index + 1,
      bookingId: item.bookingId || '',
      customerName: item.customerName || '',
      plotNo: item.inventoryName || '',
      plotAmount: plotAmount,
      commissionGeneratedAmount: item.commissionGeneratedAmount || 0,
      commissionRate: item.netCommissionPercent || 0,
      commissionAmount: item.netCommissionAmount || 0,
      associate: item.associateName || '',
      status: item.due > 0 ? 'Pending' : 'Paid',
      // Additional fields from API
      associateCommissionPercent: item.associateCommissionPercent || 0,
      bookingCommissionPercent: item.bookingCommissionPercent || 0,
      discountAmount: item.discountAmount || 0,
      commissionAmountpaid: item.commissionAmountpaid || 0,
      due: item.due || 0,
      availableAmount: item.availableAmount || 0,
      projectName: item.projectName || '',
      inventoryType: item.inventoryType || '',
      bookingDate: item.bookingDate || '',
      bookingObjectId: item.bookingObjectId || '',
      createdAt: item.createdAt || '',
      _raw: item, // Store raw data for dialog
    };
  };

  // Fetch booking commission data
  const fetchCommissionData = useCallback(async (overrides = {}) => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
      };

      const effFilters = overrides.filters !== undefined ? overrides.filters : filters;
      const effSelectedAssociate = overrides.selectedAssociate !== undefined ? overrides.selectedAssociate : selectedAssociate;
      const effSelectedBooking = overrides.selectedBooking !== undefined ? overrides.selectedBooking : selectedBooking;
      const effSelectedCustomer = overrides.selectedCustomer !== undefined ? overrides.selectedCustomer : selectedCustomer;
      const effSortBy = overrides.sortBy !== undefined ? overrides.sortBy : sortBy;

      // Add associate filter if selected
      if (effSelectedAssociate && effSelectedAssociate.id) {
        params.associateId = effSelectedAssociate.id;
      }

      // Add booking filter if selected
      if (effSelectedBooking && effSelectedBooking.id) {
        params.bookingId = effSelectedBooking.id;
      }

      // Add customer filter if selected
      if (effSelectedCustomer && effSelectedCustomer.id) {
        params.customerId = effSelectedCustomer.id;
      }

      // Add status filter if selected
      if (effFilters.status) {
        params.status = effFilters.status.toLowerCase();
      }

      // Add Date filter if selected
      if (effFilters.date) {
        params.date = effFilters.date;
      }

      // Add From Date and To Date
      if (effFilters.fromDate) {
        params.fromDate = effFilters.fromDate;
      }
      if (effFilters.toDate) {
        params.toDate = effFilters.toDate;
      }

      // Sort Params
      let apiSortBy = 'receivedDate';
      let apiSortOrder = 'desc';
      if (effSortBy) {
        if (effSortBy.endsWith('Desc')) {
          apiSortBy = effSortBy.replace('Desc', '');
          apiSortOrder = 'desc';
        } else if (effSortBy.endsWith('Asc')) {
          apiSortBy = effSortBy.replace('Asc', '');
          apiSortOrder = 'asc';
        }
      }
      params.sortBy = apiSortBy;
      params.sortOrder = apiSortOrder;

      const response = await getBookingCommission(params);

      let list = [];
      if (response && Array.isArray(response.data)) {
        list = response.data;
      } else if (response && Array.isArray(response.docs)) {
        list = response.docs;
      } else if (Array.isArray(response)) {
        list = response;
      }

      if (list.length > 0 || (response && (response.success || response.status === true))) {
        const mappedData = list.map((item, index) => mapCommissionData(item, index, page, rowsPerPage));
        setCommissionData(mappedData);
        setTotalCount(parseInt(response.extra?.total || response.totalItems || response.count || list.length || 0, 10));
      } else {
        setCommissionData([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching booking commission:', error);
      setCommissionData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, selectedAssociate, selectedBooking, selectedCustomer, filters, sortBy]);

  // Fetch customers for autocomplete with pagination
  const fetchCustomers = useCallback(async (searchTerm = '', pageNum = 1, append = false) => {
    if (customerFetchingRef.current) return;
    if (!append && !customerHasMore && pageNum > 1) return;
    
    customerFetchingRef.current = true;
    setLoadingCustomers(true);
    try {
      const base = process.env.REACT_APP_API_BASE_URL;
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: String(pageNum),
        limit: '20',
      });
      
      if (searchTerm && searchTerm.trim().length > 0) {
        params.append('search', searchTerm.trim());
      }

      const response = await fetch(`${base}/api/customers?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      
      const result = await response.json();
      
      let customers = [];
      let total = 0;
      
      if (result.success && Array.isArray(result.data)) {
        customers = result.data;
        total = parseInt(result.extra?.total || customers.length, 10);
      }

      const formattedCustomers = customers.map((customer) => ({
        id: customer._id || customer.id,
        name: customer.name || '',
        number: String(customer.customerNo || ''),
        mobile: String(customer.phone || customer.whatsapp || ''),
        email: customer.email || '',
        customerId: customer._id || customer.id,
        _raw: customer,
      }));

      if (append) {
        setCustomerOptions(prev => {
          const newOptions = [...prev, ...formattedCustomers];
          const currentTotal = newOptions.length;
          const hasMore = currentTotal < total && formattedCustomers.length >= 20;
          setCustomerHasMore(hasMore);
          return newOptions;
        });
      } else {
        setCustomerOptions(formattedCustomers);
        const currentTotal = formattedCustomers.length;
        const hasMore = currentTotal < total && formattedCustomers.length >= 20;
        setCustomerHasMore(hasMore);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
      if (!append) {
        setCustomerOptions([]);
        setCustomerHasMore(false);
      }
    } finally {
      setLoadingCustomers(false);
      customerFetchingRef.current = false;
    }
  }, []);

  // Pre-load customers on mount so dropdown is ready immediately
  useEffect(() => {
    fetchCustomers('', 1, false);
    customerInitialLoadRef.current = true;
  }, [fetchCustomers]);

  // Debounced customer search — fires on any input (no minimum chars)
  useEffect(() => {
    const trimmedInput = customerSearchInput.trim();

    // If cleared, restore full list
    if (trimmedInput.length === 0) {
      if (customerInitialLoadRef.current && customerSearchTermRef.current !== '') {
        customerSearchTermRef.current = '';
        setCustomerPage(1);
        setCustomerHasMore(true);
        fetchCustomers('', 1, false);
      }
      return;
    }

    if (customerSearchTermRef.current !== trimmedInput) {
      setCustomerPage(1);
      setCustomerHasMore(true);
      customerSearchTermRef.current = trimmedInput;
    }

    const timeoutId = setTimeout(() => {
      fetchCustomers(trimmedInput, 1, false);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [customerSearchInput, fetchCustomers]);

  // Fetch associates for autocomplete with pagination
  const fetchAssociates = useCallback(async (searchTerm = '', pageNum = 1, append = false) => {
    if (associateFetchingRef.current) return;
    if (!append && !associateHasMore && pageNum > 1) return;
    
    associateFetchingRef.current = true;
    setLoadingAssociates(true);
    try {
      const params = {
        page: pageNum,
        limit: 20, // Default limit for pagination
      };
      
      if (searchTerm && searchTerm.trim().length > 0) {
        params.filterKey = searchTerm.trim();
      }

      const response = await getAssociates(params);
      
      let associates = [];
      let total = 0;
      
      // Extract data and total from response
      if (response.status && Array.isArray(response.data)) {
        associates = response.data;
        // Check for total in extra object first, then fallback to other locations
        total = parseInt(response.extra?.total || response.totalCount || response.data?.totalDocs || response.count || associates.length, 10);
      } else if (response.success && Array.isArray(response.data)) {
        associates = response.data;
        total = parseInt(response.extra?.total || response.totalCount || response.data?.totalDocs || response.count || associates.length, 10);
      } else if (Array.isArray(response.data?.docs)) {
        associates = response.data.docs;
        total = parseInt(response.extra?.total || response.data.totalDocs || response.totalCount || associates.length, 10);
      }

      const formattedAssociates = associates.map((associate) => ({
        id: associate._id || associate.id,
        name: associate.name || '',
        number: String(associate.associateId || associate._id || ''),
        phone: String(associate.phone || associate.whatsapp || ''),
      }));

      if (append) {
        setAssociateOptions(prev => {
          const newOptions = [...prev, ...formattedAssociates];
          const currentTotal = newOptions.length;
          // Check if there are more pages: current total < total AND we got a full page of results
          const hasMore = currentTotal < total && formattedAssociates.length >= params.limit;
          setAssociateHasMore(hasMore);
          return newOptions;
        });
      } else {
        setAssociateOptions(formattedAssociates);
        const currentTotal = formattedAssociates.length;
        // Check if there are more pages: current total < total AND we got a full page of results
        const hasMore = currentTotal < total && formattedAssociates.length >= params.limit;
        setAssociateHasMore(hasMore);
      }
    } catch (error) {
      console.error('Error fetching associates:', error);
      if (!append) {
        setAssociateOptions([]);
        setAssociateHasMore(false);
      }
    } finally {
      setLoadingAssociates(false);
      associateFetchingRef.current = false;
    }
  }, []);

  // Fetch bookings for autocomplete with pagination
  const fetchBookings = useCallback(async (searchTerm = '', pageNum = 1, append = false) => {
    if (bookingFetchingRef.current) return;
    if (!append && !bookingHasMore && pageNum > 1) return;
    
    bookingFetchingRef.current = true;
    setLoadingBookings(true);
    try {
      const params = {
        page: pageNum,
        limit: 20, // Default limit for pagination
      };
      
      if (searchTerm && searchTerm.trim().length > 0) {
        params.search = searchTerm.trim();
      }

      const response = await getBookings(params);
      
      let bookings = [];
      let total = 0;
      
      if (response.success || response.status === true) {
        bookings = Array.isArray(response.data) ? response.data : (response.data?.docs || []);
        total = response.totalCount || response.data?.totalDocs || response.count || bookings.length;
      } else if (Array.isArray(response)) {
        bookings = response;
        total = bookings.length;
      } else if (response.data) {
        bookings = Array.isArray(response.data) ? response.data : (response.data?.docs || []);
        total = response.totalCount || response.data?.totalDocs || response.count || bookings.length;
      }

      const formattedBookings = bookings.map((booking) => ({
        id: booking._id || booking.id || booking.bookingId,
        bookingId: booking.bookingId || booking.bookingNo || '',
        customerName: booking.customerName || booking.customer?.name || '',
        displayLabel: `Booking #${booking.bookingId || booking.bookingNo || ''} - ${booking.customerName || booking.customer?.name || ''}`,
      }));

      if (append) {
        setBookingOptions(prev => {
          const newOptions = [...prev, ...formattedBookings];
          // Check if there are more pages
          const hasMore = newOptions.length < total && formattedBookings.length === params.limit;
          setBookingHasMore(hasMore);
          return newOptions;
        });
      } else {
        setBookingOptions(formattedBookings);
        // Check if there are more pages
        const hasMore = formattedBookings.length < total && formattedBookings.length === params.limit;
        setBookingHasMore(hasMore);
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
      if (!append) {
        setBookingOptions([]);
        setBookingHasMore(false);
      }
    } finally {
      setLoadingBookings(false);
      bookingFetchingRef.current = false;
    }
  }, []);

  // Initial load and when filters change
  useEffect(() => {
    fetchCommissionData();
  }, [fetchCommissionData]);

  // Debounced associate search - only fetch when user types (length >= 2)
  useEffect(() => {
    const trimmedInput = associateSearchInput.trim();
    
    // Don't fetch on initial empty load or if input is too short
    if (trimmedInput.length === 0) {
      // Only fetch on initial load once
      if (!associateInitialLoadRef.current) {
        associateInitialLoadRef.current = true;
        // Don't fetch empty - wait for user input
        return;
      }
      return;
    }

    // Only fetch if user has typed at least 2 characters
    if (trimmedInput.length < 2) {
      return;
    }

    // Reset pagination when search term changes
    if (associateSearchTermRef.current !== trimmedInput) {
      setAssociatePage(1);
      setAssociateHasMore(true);
      associateSearchTermRef.current = trimmedInput;
    }

    const timeoutId = setTimeout(() => {
      fetchAssociates(trimmedInput, 1, false);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [associateSearchInput, fetchAssociates]);

  // Debounced booking search - only fetch when user types (length >= 2)
  useEffect(() => {
    const trimmedInput = bookingSearchInput.trim();
    
    // Don't fetch on initial empty load or if input is too short
    if (trimmedInput.length === 0) {
      // Only fetch on initial load once
      if (!bookingInitialLoadRef.current) {
        bookingInitialLoadRef.current = true;
        // Don't fetch empty - wait for user input
        return;
      }
      return;
    }

    // Only fetch if user has typed at least 2 characters
    if (trimmedInput.length < 2) {
      return;
    }

    // Reset pagination when search term changes
    if (bookingSearchTermRef.current !== trimmedInput) {
      setBookingPage(1);
      setBookingHasMore(true);
      bookingSearchTermRef.current = trimmedInput;
    }

    const timeoutId = setTimeout(() => {
      fetchBookings(trimmedInput, 1, false);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [bookingSearchInput, fetchBookings]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleReset = () => {
    setFilters({
      fromDate: '',
      toDate: '',
      status: '',
      associate: '',
      searchTerm: '',
      date: '',
    });
    setSortBy('');
    setTempSortBy('');
    setTempFromDate('');
    setTempToDate('');
    setTempDate('');
    setSelectedAssociate(null);
    setSelectedBooking(null);
    setSelectedCustomer(null);
    setTempSelectedBooking(null);
    setTempSelectedCustomer(null);
    setAssociateSearchInput('');
    setBookingSearchInput('');
    setCustomerSearchInput('');
    setPage(0);
  };

  const handleApplyFilters = () => {
    setSortBy(tempSortBy);
    setSelectedBooking(tempSelectedBooking);
    setSelectedCustomer(tempSelectedCustomer);
    
    const updatedFilters = {
      ...filters,
      fromDate: tempFromDate,
      toDate: tempToDate,
      date: tempDate,
    };
    setFilters(updatedFilters);
    setPage(0);
    setMoreFiltersOpen(false);
  };

  const handleResetFilters = () => {
    setSortBy('');
    setTempSortBy('');
    setTempFromDate('');
    setTempToDate('');
    setTempDate('');
    setTempSelectedBooking(null);
    setTempSelectedCustomer(null);
    
    const cleared = {
      ...filters,
      fromDate: '',
      toDate: '',
      date: '',
    };
    setFilters(cleared);
    setSelectedBooking(null);
    setSelectedCustomer(null);
    setPage(0);
  };

  const handleSearch = () => {
    setPage(0);
    fetchCommissionData();
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Export functions

  const handleOpenExportDialog = (type) => {
    setExportType(type);
    setExportDialogOpen(true);
    setExportFromDate(filters.fromDate || '');
    setExportToDate(filters.toDate || '');
    setExportDateMode('current');
  };

  const handleCloseExportDialog = () => {
    setExportDialogOpen(false);
    setExportType('');
    setExportLoading(false);
  };

  const handleConfirmExport = async () => {
    setExportLoading(true);
    try {
      const params = {
        export: 'true',
      };

      if (selectedAssociate && selectedAssociate.id) {
        params.associateId = selectedAssociate.id;
      }
      if (selectedBooking && selectedBooking.id) {
        params.bookingId = selectedBooking.id;
      }
      if (selectedCustomer && selectedCustomer.id) {
        params.customerId = selectedCustomer.id;
      }
      if (filters.status) {
        params.status = filters.status.toLowerCase();
      }

      // Sort Params
      let apiSortBy = 'receivedDate';
      let apiSortOrder = 'desc';
      if (sortBy) {
        if (sortBy.endsWith('Desc')) {
          apiSortBy = sortBy.replace('Desc', '');
          apiSortOrder = 'desc';
        } else if (sortBy.endsWith('Asc')) {
          apiSortBy = sortBy.replace('Asc', '');
          apiSortOrder = 'asc';
        }
      }
      params.sortBy = apiSortBy;
      params.sortOrder = apiSortOrder;

      // Apply filters only if NOT in "All Dates (Full History)" mode
      if (exportDateMode !== 'all') {
        if (exportDateMode === 'current') {
          if (filters.fromDate) params.fromDate = filters.fromDate;
          if (filters.toDate) params.toDate = filters.toDate;
          if (filters.date) params.date = filters.date;
        } else if (exportDateMode === 'custom') {
          if (exportFromDate) params.fromDate = exportFromDate;
          if (exportToDate) params.toDate = exportToDate;
        } else if (exportDateMode === 'single') {
          if (exportFromDate) {
            params.date = exportFromDate;
          }
        }
      }

      const response = await getBookingCommission(params);

      let list = [];
      if (response && Array.isArray(response.data)) {
        list = response.data;
      } else if (response && Array.isArray(response.docs)) {
        list = response.docs;
      } else if (Array.isArray(response)) {
        list = response;
      }

      if (list.length === 0) {
        setAlertModal({ open: true, title: 'Warning', message: 'No data found for the selected filter/date range.' });
        setExportLoading(false);
        return;
      }

      const mappedList = list.map((item, index) => mapCommissionData(item, index, 0, list.length));

      const rows = mappedList.map((commission, index) => ({
        'S.No': index + 1,
        'Booking ID': commission.bookingId || '',
        'Customer': commission.customerName || '',
        'Property Type': commission.inventoryType || '',
        'Plot No': commission.plotNo || '',
        'Plot Amount': commission.plotAmount || 0,
        'Commission Generated Amount': commission.commissionGeneratedAmount || 0,
        'Commission %': commission.commissionRate || 0,
        'Commission Amount': commission.commissionAmount || 0,
        'Associate': commission.associate || '',
        'Status': commission.status || '',
      }));

      const fileName = `booking_commissions_${new Date().toISOString().split('T')[0]}`;

      if (exportType === 'csv') {
        exportAsCSV({ rows, fileNameBase: fileName });
      } else if (exportType === 'pdf') {
        const formattedRows = rows.map(r => ({
          ...r,
          'Plot Amount': `Rs. ${r['Plot Amount'].toLocaleString('en-IN')}`,
          'Commission Generated Amount': `Rs. ${r['Commission Generated Amount'].toLocaleString('en-IN')}`,
          'Commission Amount': `Rs. ${r['Commission Amount'].toLocaleString('en-IN')}`,
        }));
        exportAsPDF({ rows: formattedRows, title: 'Booking Commission Report', fileNameBase: fileName, orientation: 'l' });
      } else if (exportType === 'excel') {
        exportAsExcel({ rows, sheetName: 'Booking Commissions', fileNameBase: fileName });
      }

      handleCloseExportDialog();
    } catch (error) {
      console.error('Export failed:', error);
      showSnackbar({ type: 'error', message: 'Export failed. Please try again.' });
    } finally {
      setExportLoading(false);
    }
  };

  // View function
  const handleView = (row) => {
    setSelectedCommission(row);
    setViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedCommission(null);
  };

  // Filter data based on current filters (client-side filtering for status and search term)
  const filteredData = useMemo(() => {
    return commissionData.filter(item => {
    const matchesStatus = filters.status === '' || item.status === filters.status;
    const matchesSearch = filters.searchTerm === '' || 
      item.customerName.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
        String(item.bookingId).toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
      item.plotNo.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
        item.associate.toLowerCase().includes(filters.searchTerm.toLowerCase());
      
      return matchesStatus && matchesSearch;
    });
  }, [commissionData, filters.status, filters.searchTerm]);

  const totalCommission = useMemo(() => {
    return filteredData.reduce((sum, item) => sum + item.commissionAmount, 0);
  }, [filteredData]);
  
  const paidCommission = useMemo(() => {
    return filteredData.filter(item => item.status === 'Paid').reduce((sum, item) => sum + item.commissionAmount, 0);
  }, [filteredData]);
  
  const pendingCommission = useMemo(() => {
    return filteredData.filter(item => item.status === 'Pending').reduce((sum, item) => sum + item.commissionAmount, 0);
  }, [filteredData]);

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
              Booking Commission Report
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Track commission payments for bookings
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'center' }}>
            <ExportButton
              onExportPdf={() => handleOpenExportDialog('pdf')}
              onExportCsv={() => handleOpenExportDialog('csv')}
              onExportExcel={() => handleOpenExportDialog('excel')}
            />
            <RefreshButton
              onClick={() => fetchCommissionData()}
              loading={loading}
              sx={{
                fontWeight: 600,
                px: 2,
                fontSize: '15px',
                borderRadius: 2,
                height: { xs: 'auto', sm: 40 }
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Summary Cards */}
      

      {/* Search Section */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Search & Filters
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3.5}>
              <Autocomplete
                options={associateOptions}
                getOptionLabel={(option) => option.name ? `${option.name} (${option.number})` : ''}
                value={selectedAssociate}
                onChange={(event, newValue) => {
                  setSelectedAssociate(newValue);
                  setPage(0);
                }}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === 'input' || reason === 'clear') {
                    setAssociateSearchInput(newInputValue);
                    // Reset pagination when input changes
                    if (reason === 'clear' || (reason === 'input' && newInputValue.trim().length < 2)) {
                      setAssociatePage(1);
                      setAssociateHasMore(true);
                      associateSearchTermRef.current = '';
                    }
                  }
                }}
                onOpen={() => {
                  // Fetch initial options when dropdown opens if we don't have any
                  if (associateOptions.length === 0 && !associateFetchingRef.current) {
                    setAssociatePage(1);
                    setAssociateHasMore(true);
                    fetchAssociates(associateSearchInput || '', 1, false);
                  }
                }}
                loading={loadingAssociates}
                ListboxProps={{
                  onScroll: (event) => {
                    const listboxNode = event.currentTarget;
                    // Check if scrolled to bottom (within 50px)
                    const scrollTop = listboxNode.scrollTop;
                    const scrollHeight = listboxNode.scrollHeight;
                    const clientHeight = listboxNode.clientHeight;
                    const scrollBottom = scrollTop + clientHeight;
                    
                    // Trigger load more when within 50px of bottom
                    if (scrollHeight > clientHeight && scrollBottom >= scrollHeight - 50) {
                      if (associateHasMore && !loadingAssociates && !associateFetchingRef.current) {
                        const nextPage = associatePage + 1;
                        setAssociatePage(nextPage);
                        fetchAssociates(associateSearchInput || '', nextPage, true);
                      }
                    }
                  },
                }}
                filterOptions={(options, { inputValue }) => {
                  const searchTerm = inputValue.toLowerCase();
                  return options.filter(option =>
                    (option.name || '').toLowerCase().includes(searchTerm) ||
                    String(option.number || '').toLowerCase().includes(searchTerm)
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Associate"
                    size="small"
                    placeholder="Type name or number..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <React.Fragment>
                          {loadingAssociates ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {option.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        {option.number} {option.phone ? `• ${option.phone}` : ''}
                      </Typography>
                    </Box>
                  </Box>
                )}
                noOptionsText="No associates found"
                clearOnEscape
                selectOnFocus
                handleHomeEndKeys
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select 
                  label="Status" 
                  value={filters.status}
                  onChange={(e) => {
                    setFilters({ ...filters, status: e.target.value });
                    setPage(0);
                  }}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="Paid">Paid</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={4} md={1.2}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleSearch}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                sx={{ height: 40, textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}
              >
                {loading ? 'Searching...' : 'Search'}
              </Button>
            </Grid>
            <Grid item xs={12} sm={4} md={1.1}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleReset}
                sx={{ height: 40, textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}
              >
                Reset
              </Button>
            </Grid>
            <Grid item xs={12} sm={4} md={2.7}>
              <Box sx={{ width: '100%' }}>
                <MoreFilters
                  expanded={moreFiltersOpen}
                  onToggle={() => setMoreFiltersOpen(!moreFiltersOpen)}
                  sortBy={tempSortBy}
                  onChangeSortBy={setTempSortBy}
                  onApply={handleApplyFilters}
                  onReset={handleResetFilters}
                  type="bookingCommission"
                  height="40px"
                >
                  <Grid container spacing={1.5}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="From Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={tempFromDate}
                        onChange={(e) => {
                          const val = e.target.value;
                          setTempFromDate(val);
                          if (val) setTempDate('');
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="To Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={tempToDate}
                        onChange={(e) => {
                          const val = e.target.value;
                          setTempToDate(val);
                          if (val) setTempDate('');
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={tempDate}
                        onChange={(e) => {
                          const val = e.target.value;
                          setTempDate(val);
                          if (val) {
                            setTempFromDate('');
                            setTempToDate('');
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Autocomplete
                        options={bookingOptions}
                        getOptionLabel={(option) => option.displayLabel || `Booking #${option.bookingId}`}
                        value={tempSelectedBooking}
                        onChange={(event, newValue) => {
                          setTempSelectedBooking(newValue);
                        }}
                        onInputChange={(event, newInputValue, reason) => {
                          if (reason === 'input' || reason === 'clear') {
                            setBookingSearchInput(newInputValue);
                            // Reset pagination when input changes
                            if (reason === 'clear' || (reason === 'input' && newInputValue.trim().length < 2)) {
                              setBookingPage(1);
                              setBookingHasMore(true);
                              bookingSearchTermRef.current = '';
                            }
                          }
                        }}
                        onOpen={() => {
                          // Fetch initial options when dropdown opens if we don't have any
                          if (bookingOptions.length === 0 && !bookingFetchingRef.current) {
                            setBookingPage(1);
                            setBookingHasMore(true);
                            fetchBookings(bookingSearchInput || '', 1, false);
                          }
                        }}
                        loading={loadingBookings}
                        ListboxProps={{
                          onScroll: (event) => {
                            const listboxNode = event.currentTarget;
                            // Check if scrolled to bottom (within 50px)
                            if (listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 50) {
                              if (bookingHasMore && !loadingBookings && !bookingFetchingRef.current) {
                                const nextPage = bookingPage + 1;
                                setBookingPage(nextPage);
                                fetchBookings(bookingSearchInput || '', nextPage, true);
                              }
                            }
                          },
                        }}
                        filterOptions={(options, { inputValue }) => {
                          return options.filter(option =>
                            String(option.bookingId).toLowerCase().includes(inputValue.toLowerCase()) ||
                            option.customerName.toLowerCase().includes(inputValue.toLowerCase())
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Search Booking"
                            size="small"
                            placeholder="Type booking ID or customer name..."
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon color="action" />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <React.Fragment>
                                  {loadingBookings ? <CircularProgress color="inherit" size={20} /> : null}
                                  {params.InputProps.endAdornment}
                                </React.Fragment>
                              ),
                            }}
                          />
                        )}
                        renderOption={(props, option) => (
                          <Box component="li" {...props}>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                Booking #{option.bookingId}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                {option.customerName}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                        noOptionsText="No bookings found"
                        clearOnEscape
                        selectOnFocus
                        handleHomeEndKeys
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Autocomplete
                        options={customerOptions}
                        getOptionLabel={(option) => option.name ? `${option.name} (${option.number})` : ''}
                        value={tempSelectedCustomer}
                        onChange={(event, newValue) => {
                          setTempSelectedCustomer(newValue);
                        }}
                        onInputChange={(event, newInputValue, reason) => {
                          if (reason === 'input' || reason === 'clear') {
                            setCustomerSearchInput(newInputValue);
                          }
                        }}
                        onOpen={() => {
                          if (customerOptions.length === 0 && !customerFetchingRef.current) {
                            fetchCustomers('', 1, false);
                          }
                        }}
                        loading={loadingCustomers}
                        ListboxProps={{
                          onScroll: (event) => {
                            const listboxNode = event.currentTarget;
                            const scrollTop = listboxNode.scrollTop;
                            const scrollHeight = listboxNode.scrollHeight;
                            const clientHeight = listboxNode.clientHeight;
                            const scrollBottom = scrollTop + clientHeight;
                            
                            if (scrollHeight > clientHeight && scrollBottom >= scrollHeight - 50) {
                              if (customerHasMore && !loadingCustomers && !customerFetchingRef.current) {
                                const nextPage = customerPage + 1;
                                setCustomerPage(nextPage);
                                fetchCustomers(customerSearchInput || '', nextPage, true);
                              }
                            }
                          },
                        }}
                        filterOptions={(options, { inputValue }) => {
                          const searchTerm = inputValue.toLowerCase();
                          return options.filter(option =>
                            (option.name || '').toLowerCase().includes(searchTerm) ||
                            String(option.number || '').toLowerCase().includes(searchTerm) ||
                            String(option.mobile || '').includes(searchTerm)
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Search Customer"
                            size="small"
                            placeholder="Type name, number, or mobile..."
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon color="action" />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <React.Fragment>
                                  {loadingCustomers ? <CircularProgress color="inherit" size={20} /> : null}
                                  {params.InputProps.endAdornment}
                                </React.Fragment>
                              ),
                            }}
                          />
                        )}
                        renderOption={(props, option) => (
                          <Box component="li" {...props}>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {option.name}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                {option.number} {option.mobile ? `• ${option.mobile}` : ''}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                        noOptionsText="No customers found"
                        clearOnEscape
                        selectOnFocus
                      />
                    </Grid>
                  </Grid>
                </MoreFilters>
              </Box>
            </Grid>
          </Grid>
          
          {/* Search Results Summary */}
          {(selectedAssociate || selectedBooking || filters.searchTerm || filters.status) && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#666' }}>
                {filteredData.length} commission record{filteredData.length !== 1 ? 's' : ''} found
                {selectedAssociate && ` for ${selectedAssociate.name}`}
                {selectedBooking && ` for booking #${selectedBooking.bookingId}`}
                {filters.searchTerm && ` matching "${filters.searchTerm}"`}
                {filters.status && ` with status "${filters.status}"`}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Table */}
      <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2, mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Commission Details
          </Typography>
          <TableContainer>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#536DFE' }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>S.No</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Booking ID</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Customer</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Property Type</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Property No</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Property Amount</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Commission Generated Amount</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Commission %</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Commission Amount</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Associate</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Status</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No commission records found matching your search criteria
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                    filteredData.map((row, index) => (
                    <TableRow 
                      key={row.sno} 
                      hover
                      sx={{
                        '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' },
                        '&:hover': { backgroundColor: '#f0f7ff !important' }
                      }}
                    >
                        <TableCell>{row.sno}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <Button
                          variant="text"
                          size="small"
                          sx={{ 
                            fontWeight: 600, 
                            textTransform: 'none', 
                            p: 0,
                            minWidth: 0,
                            color: 'primary.main',
                            '&:hover': {
                              textDecoration: 'underline',
                              backgroundColor: 'transparent'
                            }
                          }}
                          onClick={() => {
                            if (row.bookingObjectId) {
                              history.push(`/app/booking/BookingDetails/${row.bookingObjectId}`);
                            }
                          }}
                        >
                          {row.bookingId}
                        </Button>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.customerName}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.inventoryType}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.plotNo}</TableCell>
                      <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Rs. {row.plotAmount.toLocaleString('en-IN')}</TableCell>
                      <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Rs. {row.commissionGeneratedAmount.toLocaleString('en-IN')}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.commissionRate}%</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#2e7d32', whiteSpace: 'nowrap' }}>Rs. {row.commissionAmount.toLocaleString('en-IN')}</TableCell>
                      <TableCell>{row.associate}</TableCell>
                      <TableCell>
                        <Chip 
                          label={row.status} 
                          size="small" 
                          color={row.status === 'Paid' ? 'success' : 'warning'}
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleView(row)}
                            sx={{ color: '#536DFE' }}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            )}
          </TableContainer>
          
          {/* Pagination */}
          {!loading && totalCount > 0 && (
            <TablePagination
              
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </CardContent>
      </Card>

      {/* Commission Details Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          backgroundColor: '#536DFE',
          color: 'white',
          fontWeight: 700
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Visibility />
            <Typography variant="h6">Commission Details</Typography>
          </Box>
          <IconButton
            onClick={handleCloseViewDialog}
            sx={{ color: 'white' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {selectedCommission && (
            <Box>
              {/* Header Section */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                <Avatar sx={{ backgroundColor: '#536DFE', mr: 2, width: 56, height: 56 }}>
                  <CurrencyRupeeIcon sx={{ fontSize: 32 }} />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#333', mb: 0.5 }}>
                    {selectedCommission.customerName}
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#666', fontWeight: 600 }}>
                    Booking ID: {selectedCommission.bookingId}
                  </Typography>
                  <Chip 
                    label={selectedCommission.status} 
                    size="small" 
                    color={selectedCommission.status === 'Paid' ? 'success' : 'warning'}
                    sx={{ mt: 1, fontWeight: 600 }}
                  />
                </Box>
              </Box>

              <Divider sx={{ mb: 3 }} />

              {/* Details Grid */}
              <Grid container spacing={3}>
                {/* Booking Information */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#536DFE', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarToday />
                        Booking Details
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Booking ID:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedCommission.bookingId}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Customer Name:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedCommission.customerName}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Plot Number:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedCommission.plotNo}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Associate:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedCommission.associate}</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Financial Information */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#536DFE', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CurrencyRupeeIcon />
                        Commission Details
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Plot Amount:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                            ₹{selectedCommission.plotAmount.toLocaleString('en-IN')}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Commission Rate:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {selectedCommission.commissionRate}%
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Commission Amount:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', color: '#2e7d32' }}>
                            ₹{selectedCommission.commissionAmount.toLocaleString('en-IN')}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Status:</Typography>
                          <Chip 
                            label={selectedCommission.status} 
                            size="small" 
                            color={selectedCommission.status === 'Paid' ? 'success' : 'warning'}
                            sx={{ fontWeight: 600 }}
                          />
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Summary Information */}
                <Grid item xs={12}>
                  <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#536DFE', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Person />
                        Summary
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                            <Typography variant="h4" sx={{ fontWeight: 700, color: '#536DFE' }}>
                              {selectedCommission.commissionRate}%
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                              Commission Rate
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                            <Typography variant="h4" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                              ₹{selectedCommission.commissionAmount.toLocaleString('en-IN')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                              Commission Earned
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                            <Typography variant="h4" sx={{ fontWeight: 700, color: '#333' }}>
                              ₹{selectedCommission.plotAmount.toLocaleString('en-IN')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                              Total Plot Value
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
          <Button
            onClick={handleCloseViewDialog}
            variant="contained"
            sx={{
              backgroundColor: '#536DFE',
              '&:hover': { backgroundColor: '#3F51B5' },
              textTransform: 'none',
              fontWeight: 600,
              px: 3
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Date Range Dialog */}
      <Dialog
        open={exportDialogOpen}
        onClose={handleCloseExportDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 16px 48px rgba(83, 109, 254, 0.15), 0 2px 12px rgba(0,0,0,0.06)',
          }
        }}
      >
        {/* Gradient Header */}
        <Box sx={{
          background: 'linear-gradient(135deg, #536DFE 0%, #3d5afe 50%, #304ffe 100%)',
          px: 2.5, py: 1.8,
          position: 'relative', overflow: 'hidden',
        }}>
          <Box sx={{ position: 'absolute', top: -15, right: -15, width: 55, height: 55, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.08)' }} />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
              <Box sx={{
                width: 34, height: 34, borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.18)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <FileDownload sx={{ color: '#fff', fontSize: 18 }} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, color: '#fff', fontSize: '0.95rem', lineHeight: 1.2 }}>
                  Export Booking Commission Report
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.68rem', mt: 0.2 }}>
                  Download your report data
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={handleCloseExportDialog} size="small" sx={{ color: 'rgba(255,255,255,0.8)', p: 0.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>

        <DialogContent sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
          {/* Format Badge */}
          <FormatBadge type={exportType} />

          {/* Section label */}
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#555', mb: 1, textTransform: 'uppercase', letterSpacing: 0.6 }}>
            Select Date Range
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
            {/* Option 1: Current Filters */}
            <Box
              onClick={() => setExportDateMode('current')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.2, borderRadius: 2, cursor: 'pointer',
                border: exportDateMode === 'current' ? '1.5px solid #536DFE' : '1.5px solid #e8eaf6',
                bgcolor: exportDateMode === 'current' ? 'rgba(83, 109, 254, 0.04)' : '#fff',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83, 109, 254, 0.02)' },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: exportDateMode === 'current' ? '#536DFE' : '#e8eaf6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease', flexShrink: 0,
              }}>
                <FilterAlt sx={{ fontSize: 16, color: exportDateMode === 'current' ? '#fff' : '#536DFE' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1a1a2e' }}>
                  Current Filters
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#999', mt: 0.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {filters.fromDate && filters.toDate
                    ? `${filters.fromDate} → ${filters.toDate}`
                    : filters.fromDate ? `From ${filters.fromDate}` : filters.toDate ? `Till ${filters.toDate}` : 'All dates'}
                </Typography>
              </Box>
              <Radio checked={exportDateMode === 'current'} size="small" sx={{ p: 0.3, color: '#c5cae9', '&.Mui-checked': { color: '#536DFE' } }} />
            </Box>

            {/* Option 2: Custom Date Range */}
            <Box
              onClick={() => setExportDateMode('custom')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.2, borderRadius: 2, cursor: 'pointer',
                border: exportDateMode === 'custom' ? '1.5px solid #536DFE' : '1.5px solid #e8eaf6',
                bgcolor: exportDateMode === 'custom' ? 'rgba(83, 109, 254, 0.04)' : '#fff',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83, 109, 254, 0.02)' },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: exportDateMode === 'custom' ? '#536DFE' : '#e8eaf6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease', flexShrink: 0,
              }}>
                <DateRange sx={{ fontSize: 16, color: exportDateMode === 'custom' ? '#fff' : '#536DFE' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1a1a2e' }}>
                  Custom Date Range
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#999', mt: 0.1 }}>
                  Choose from & to dates
                </Typography>
              </Box>
              <Radio checked={exportDateMode === 'custom'} size="small" sx={{ p: 0.3, color: '#c5cae9', '&.Mui-checked': { color: '#536DFE' } }} />
            </Box>

            {/* Option 3: Single Day */}
            <Box
              onClick={() => setExportDateMode('single')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.2, borderRadius: 2, cursor: 'pointer',
                border: exportDateMode === 'single' ? '1.5px solid #536DFE' : '1.5px solid #e8eaf6',
                bgcolor: exportDateMode === 'single' ? 'rgba(83, 109, 254, 0.04)' : '#fff',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83, 109, 254, 0.02)' },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: exportDateMode === 'single' ? '#536DFE' : '#e8eaf6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease', flexShrink: 0,
              }}>
                <Today sx={{ fontSize: 16, color: exportDateMode === 'single' ? '#fff' : '#536DFE' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1a1a2e' }}>
                  Single Day
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#999', mt: 0.1 }}>
                  Export for one specific date
                </Typography>
              </Box>
              <Radio checked={exportDateMode === 'single'} size="small" sx={{ p: 0.3, color: '#c5cae9', '&.Mui-checked': { color: '#536DFE' } }} />
            </Box>

            {/* Option 4: All Dates (Full History) */}
            <Box
              onClick={() => setExportDateMode('all')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.2, borderRadius: 2, cursor: 'pointer',
                border: exportDateMode === 'all' ? '1.5px solid #536DFE' : '1.5px solid #e8eaf6',
                bgcolor: exportDateMode === 'all' ? 'rgba(83, 109, 254, 0.04)' : '#fff',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83, 109, 254, 0.02)' },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: exportDateMode === 'all' ? '#536DFE' : '#e8eaf6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease', flexShrink: 0,
              }}>
                <CalendarMonth sx={{ fontSize: 16, color: exportDateMode === 'all' ? '#fff' : '#536DFE' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1a1a2e' }}>
                  All Data (Full History)
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#999', mt: 0.1 }}>
                  Export full database history without any filters
                </Typography>
              </Box>
              <Radio checked={exportDateMode === 'all'} size="small" sx={{ p: 0.3, color: '#c5cae9', '&.Mui-checked': { color: '#536DFE' } }} />
            </Box>
          </Box>

          {/* Date inputs — compact animated */}
          {exportDateMode === 'custom' && (
            <Box sx={{
              display: 'flex', gap: 1.5, mt: 1.5,
              p: 1.5, borderRadius: 2,
              bgcolor: '#fafbff', border: '1px solid #e8eaf6',
              animation: 'fadeIn 0.2s ease',
              '@keyframes fadeIn': { '0%': { opacity: 0, transform: 'translateY(-4px)' }, '100%': { opacity: 1, transform: 'translateY(0)' } },
            }}>
              <TextField
                label="From Date" type="date"
                value={exportFromDate}
                onChange={(e) => setExportFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: '#fff', fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontSize: '0.78rem' } }}
              />
              <TextField
                label="To Date" type="date"
                value={exportToDate}
                onChange={(e) => setExportToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: '#fff', fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontSize: '0.78rem' } }}
              />
            </Box>
          )}

          {exportDateMode === 'single' && (
            <Box sx={{
              mt: 1.5, p: 1.5, borderRadius: 2,
              bgcolor: '#fafbff', border: '1px solid #e8eaf6',
              animation: 'fadeIn 0.2s ease',
              '@keyframes fadeIn': { '0%': { opacity: 0, transform: 'translateY(-4px)' }, '100%': { opacity: 1, transform: 'translateY(0)' } },
            }}>
              <TextField
                label="Select Date" type="date"
                value={exportFromDate}
                onChange={(e) => setExportFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: '#fff', fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontSize: '0.78rem' } }}
              />
            </Box>
          )}
        </DialogContent>

        {/* Actions */}
        <Box sx={{ px: 2.5, py: 1.5, bgcolor: '#fafbff', borderTop: '1px solid #eef2f6', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button
            onClick={handleCloseExportDialog}
            variant="outlined" size="small"
            sx={{
              borderRadius: 2, textTransform: 'none', fontWeight: 600,
              px: 2, fontSize: '0.8rem', borderColor: '#c5cae9', color: '#536DFE',
              '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83,109,254,0.04)' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmExport}
            variant="contained" size="small"
            disabled={exportLoading || (exportDateMode === 'custom' && (!exportFromDate || !exportToDate)) || (exportDateMode === 'single' && !exportFromDate)}
            startIcon={exportLoading ? <CircularProgress size={14} color="inherit" /> : <FileDownload sx={{ fontSize: 16 }} />}
            sx={{
              borderRadius: 2, textTransform: 'none', fontWeight: 700,
              px: 2.5, fontSize: '0.8rem',
              background: 'linear-gradient(135deg, #536DFE 0%, #3d5afe 100%)',
              boxShadow: '0 3px 10px rgba(83, 109, 254, 0.3)',
              '&:hover': { background: 'linear-gradient(135deg, #3d5afe 0%, #304ffe 100%)', boxShadow: '0 4px 14px rgba(83, 109, 254, 0.4)' },
              '&.Mui-disabled': { background: '#e0e0e0', boxShadow: 'none' },
            }}
          >
            {exportLoading ? 'Exporting...' : 'Export Now'}
          </Button>
        </Box>
      </Dialog>
      <AlertModal open={alertModal.open} title={alertModal.title} message={alertModal.message} onClose={closeAlert} />
    </Box>
  );
};

export default BookingCommission;

